/**
 * Risk Reports — look up risks by status and export the whole record.
 *
 * The screen and the export always run the same filters through the same
 * endpoint, so the workbook can never disagree with what was on screen when
 * someone pressed the button. That matters: a register export that does not
 * match its own report is worthless as evidence.
 *
 * Read-only. Nothing here changes a risk.
 */

import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const STATUS_STYLES = {
  open:        'bg-red-50 text-red-700 ring-red-200',
  monitoring:  'bg-amber-50 text-amber-800 ring-amber-200',
  mitigating:  'bg-blue-50 text-blue-700 ring-blue-200',
  accepted:    'bg-slate-100 text-slate-700 ring-slate-200',
  transferred: 'bg-violet-50 text-violet-700 ring-violet-200',
  closed:      'bg-emerald-50 text-emerald-700 ring-emerald-200',
};
const TIER_STYLES = { A: 'bg-red-600 text-white', B: 'bg-amber-500 text-white', C: 'bg-slate-400 text-white' };

const label = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const num = (v, dp = 1) => (v == null || v === '' ? '—' : Number(v).toFixed(dp));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const inputCls = 'rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-orange-300';

// Mirrors the server's mapping so the screen never labels a score with a
// formula the org does not actually use.
const METHODOLOGY_NAMES = {
  likelihood_impact_5x5: '5×5 Likelihood × Impact',
  rpn_fmea: 'RPN / FMEA',
  inherent_residual: 'Inherent / Residual',
};
const METHODOLOGY_LABELS = {
  likelihood_impact_5x5: 'Likelihood × Impact',
  rpn_fmea: 'Severity × Occurrence × Detection',
  inherent_residual: 'Severity × Likelihood × Exposure',
};

/**
 * Declared at module scope on purpose. Defined inside the component, this
 * would be a brand-new component type on every render, so React would unmount
 * and remount the inputs it wraps and the text field would lose focus after a
 * single character. Do not move it back inside.
 */
const Field = ({ label: l, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</span>
    {children}
  </label>
);

export default function RiskReports() {
  const [filters, setFilters] = useState({
    status: '', tier: '', domain_id: '', department_id: '', owner_user_id: '',
    created_from: '', created_to: '', search: '', min_residual: '', max_residual: '',
    has_open_actions: false,
  });
  const [options, setOptions] = useState({ statuses: [], tiers: [], domains: [], departments: [], owners: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/risk-assessment/reports/filters`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d?.success) setOptions(d); })
      .catch(() => {});
  }, []);

  /** One query string for the report and the export — they cannot drift. */
  const queryString = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === '' || v === false || v == null) return;
      p.append(k, String(v));
    });
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await fetch(`${API_BASE}/risk-assessment/reports?${queryString()}`, { credentials: 'include' })
        .then((r) => r.json());
      if (d?.success) setData(d);
      else setError(d?.message || 'Could not build the report.');
    } catch {
      setError('Could not reach the server.');
    }
    setLoading(false);
  }, [queryString]);

  // Debounced so typing a search term is one request when the user stops,
  // not one per character. Dropdown changes feel instant at this delay.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportExcel = async () => {
    setExporting(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/reports/export.xlsx?${queryString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Risk Register ${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('The export could not be produced. Nothing was changed.');
    }
    setExporting(false);
  };

  const reset = () => setFilters({
    status: '', tier: '', domain_id: '', department_id: '', owner_user_id: '',
    created_from: '', created_to: '', search: '', min_residual: '', max_residual: '',
    has_open_actions: false,
  });

  const set = (k) => (e) => setFilters((f) => ({
    ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const activeCount = Object.entries(filters).filter(([, v]) => v !== '' && v !== false && v != null).length;
  const risks = data?.risks || [];
  const s = data?.summary;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Risk Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Look up risks by status, owner, score or date. Export everything on record — scores, update history,
            mitigation plans — exactly as filtered.
          </p>
        </div>
        <button
          type="button" onClick={exportExcel} disabled={exporting || loading || risks.length === 0}
          title={risks.length === 0 ? 'Nothing to export with these filters' : 'Download these risks with their full history as an Excel workbook'}
          className="px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white text-sm font-semibold transition"
        >
          {exporting ? 'Building the workbook…' : `Export to Excel${risks.length ? ` (${risks.length})` : ''}`}
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-5">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          <Field label="Status">
            <select value={filters.status} onChange={set('status')} className={inputCls}>
              <option value="">All statuses</option>
              {options.statuses.map((v) => <option key={v} value={v}>{label(v)}</option>)}
            </select>
          </Field>
          <Field label="Tier">
            <select value={filters.tier} onChange={set('tier')} className={inputCls}>
              <option value="">All tiers</option>
              {options.tiers.map((v) => <option key={v} value={v}>Tier {v}</option>)}
            </select>
          </Field>
          <Field label="Domain">
            <select value={filters.domain_id} onChange={set('domain_id')} className={inputCls}>
              <option value="">All domains</option>
              {options.domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Department">
            <select value={filters.department_id} onChange={set('department_id')} className={inputCls}>
              <option value="">All departments</option>
              {options.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Owner">
            <select value={filters.owner_user_id} onChange={set('owner_user_id')} className={inputCls}>
              <option value="">Anyone</option>
              {options.owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Search">
            <input value={filters.search} onChange={set('search')} placeholder="Title or description" className={inputCls} />
          </Field>
          <Field label="Raised from">
            <input type="date" value={filters.created_from} onChange={set('created_from')} className={inputCls} />
          </Field>
          <Field label="Raised to">
            <input type="date" value={filters.created_to} onChange={set('created_to')} className={inputCls} />
          </Field>
          <Field label="Residual min">
            <input type="number" step="0.1" value={filters.min_residual} onChange={set('min_residual')} className={inputCls} />
          </Field>
          <Field label="Residual max">
            <input type="number" step="0.1" value={filters.max_residual} onChange={set('max_residual')} className={inputCls} />
          </Field>
          <label className="flex items-end gap-2 pb-1.5 col-span-2">
            <input type="checkbox" checked={filters.has_open_actions} onChange={set('has_open_actions')}
              className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
            <span className="text-sm text-slate-700">Only risks with open actions</span>
          </label>
        </div>
        {activeCount > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-slate-500">{activeCount} filter{activeCount === 1 ? '' : 's'} applied</span>
            <button type="button" onClick={reset} className="text-xs font-medium text-orange-700 hover:text-orange-800">Clear all</button>
          </div>
        )}
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {/* ── Totals ── */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Risks', value: s.total },
            { label: 'Tier A', value: s.by_tier?.A || 0 },
            { label: 'Open actions', value: s.open_actions },
            { label: 'Highest residual', value: num(s.highest_residual) },
            { label: 'Average residual', value: num(s.average_residual) },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</p>
              <p className="text-xl font-bold text-slate-900 mt-0.5">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Results ── */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <p className="py-16 text-center text-sm text-slate-500">Loading…</p>
        ) : risks.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-slate-700">No risks match these filters.</p>
            <p className="text-sm text-slate-500 mt-1">Widen the filters, or clear them to see the whole register.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Risk</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Tier</th>
                  <th className="px-3 py-2.5 font-semibold">Owner</th>
                  <th className="px-3 py-2.5 font-semibold text-right">
                    {data?.settings?.scoring_methodology === 'rpn_fmea' ? 'RPN' : 'Score'}
                  </th>
                  <th className="px-3 py-2.5 font-semibold text-right">Inherent</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Residual</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Open actions</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Updates</th>
                  <th className="px-3 py-2.5 font-semibold">Raised</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {risks.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-900">{r.risk_title}</p>
                      <p className="text-xs text-slate-500">
                        {r.domain_name || 'No domain'}
                        {Array.isArray(r.department_names) && r.department_names.length ? ` · ${r.department_names.join(', ')}` : ''}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                        {label(r.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {r.risk_tier
                        ? <span className={`inline-flex w-6 h-6 items-center justify-center rounded-md text-[11px] font-bold ${TIER_STYLES[r.risk_tier] || ''}`}>{r.risk_tier}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700">
                      {Array.isArray(r.owner_names) && r.owner_names.length ? r.owner_names.join(', ') : <span className="text-slate-400">Unassigned</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{r.rpn ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{num(r.inherent_risk)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{num(r.residual_risk)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {Number(r.open_actions) > 0
                        ? <span className="font-semibold text-amber-700">{r.open_actions}</span>
                        : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{r.update_count}</td>
                    <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data?.settings && (
        <p className="mt-3 text-xs text-slate-500">
          Scores use this organization's configured methodology,
          {' '}{METHODOLOGY_NAMES[data.settings.scoring_methodology] || data.settings.scoring_methodology}
          {' '}({METHODOLOGY_LABELS[data.settings.scoring_methodology] || 'not configured'}), scale{' '}
          {data.settings.rpn_scale}. Closed risks stay here after they leave the register. The workbook
          records the filters, who exported it and when, so two copies can always be reconciled.
        </p>
      )}
    </div>
  );
}
